/**
 * /reports/build — AI Report Builder.
 * User types a plain-English request; Claude emits a ReportDSL; we show
 * a preview table + explanation, then let them save or refine.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useRef } from "react";
import { api } from "@/lib/api";
import type { BuildResponse, ClarifyingQuestion, ReportDefinition } from "@/lib/queries";
import { reportKeys } from "@/lib/queries";

export const Route = createFileRoute("/_auth/reports/build")({
  component: ReportBuilderPage,
});

const STARTER_PROMPTS = [
  "Show me total cost by project for this year",
  "Which projects are over budget on labor?",
  "AR aging — who owes us money and how overdue?",
  "Committed cost vs budget across active projects",
  "Labor hours by cost code this month",
  "Cash position across all bank accounts",
];

function PreviewTable({
  columns,
  rows,
}: {
  columns: { name: string; type: string }[];
  rows: Record<string, unknown>[];
}) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center">
        No rows returned — the report structure looks correct, but there's no data matching the default parameters.
      </p>
    );
  }

  const fmt = (val: unknown, type: string): string => {
    if (val === null || val === undefined) return "—";
    if (type === "currency" && typeof val === "number")
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val);
    return String(val);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 dark:border-gray-700">
            {columns.map((c) => (
              <th
                key={c.name}
                className="text-left py-2 px-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap"
              >
                {c.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-slate-100 dark:border-gray-800">
              {columns.map((c) => {
                const key = c.name.replace(/ /g, "_").replace(/\//g, "_");
                const val = row[c.name] ?? row[key];
                return (
                  <td key={c.name} className="py-2 px-3 text-slate-700 dark:text-slate-300 whitespace-nowrap">
                    {fmt(val, c.type)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClarifyingQuestionsPanel({
  questions,
  onSubmit,
  isPending,
}: {
  questions: ClarifyingQuestion[];
  onSubmit: (answers: Record<string, string>) => void;
  isPending: boolean;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>(() =>
    Object.fromEntries(questions.map((q) => [q.id, ""]))
  );
  const allAnswered = questions.every((q) => answers[q.id]?.trim());

  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-5 space-y-4">
      <div>
        <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider mb-1">
          A couple of quick questions
        </p>
        <p className="text-sm text-amber-900 dark:text-amber-100">
          Answer these and I'll build the report right away.
        </p>
      </div>
      <div className="space-y-3">
        {questions.map((q) => (
          <div key={q.id}>
            <label className="block text-sm font-medium text-amber-900 dark:text-amber-100 mb-1">
              {q.question}
            </label>
            <input
              value={answers[q.id] ?? ""}
              onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && allAnswered) onSubmit(answers);
              }}
              className="w-full px-3 py-2 text-sm border border-amber-300 dark:border-amber-700 rounded-lg bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
        ))}
      </div>
      <button
        onClick={() => onSubmit(answers)}
        disabled={!allAnswered || isPending}
        className="px-5 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition"
      >
        {isPending ? "Building…" : "Build report →"}
      </button>
    </div>
  );
}

function ReportBuilderPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [prompt, setPrompt] = useState("");
  const [result, setResult] = useState<BuildResponse | null>(null);
  const [refineInput, setRefineInput] = useState("");
  const [saveName, setSaveName] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const pendingQuestions = result?.clarifying_questions?.length ? result.clarifying_questions : null;
  const hasReport = result && !pendingQuestions && result.schema_warnings.length === 0;

  const buildMutation = useMutation({
    mutationFn: ({ p, answers }: { p: string; answers?: Record<string, string> }) =>
      api<BuildResponse>("/api/reports/build", {
        method: "POST",
        body: JSON.stringify({ prompt: p, context: {}, answers: answers ?? {} }),
      }),
    onSuccess: (data) => {
      setResult(data);
      if (!data.clarifying_questions?.length) {
        setSaveName((data.dsl as Record<string, unknown>).title as string ?? "My Report");
      }
    },
  });

  const refineMutation = useMutation({
    mutationFn: (instruction: string) =>
      api<BuildResponse>("/api/reports/build/refine", {
        method: "POST",
        body: JSON.stringify({ current_dsl: result!.dsl, instruction }),
      }),
    onSuccess: (data) => {
      setResult(data);
      setRefineInput("");
    },
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      api<ReportDefinition>("/api/reports", {
        method: "POST",
        body: JSON.stringify({
          name: saveName,
          category: "custom",
          dsl: result!.dsl,
          visibility: "private",
          ai_built: true,
          ai_prompt: prompt,
        }),
      }),
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: reportKeys.catalog() });
      navigate({ to: "/reports/$reportId", params: { reportId: saved.id } });
    },
  });

  const isPending = buildMutation.isPending || refineMutation.isPending;
  const error = buildMutation.error || refineMutation.error;

  const triggerBuild = (p: string) => buildMutation.mutate({ p });
  const triggerBuildWithAnswers = (answers: Record<string, string>) =>
    buildMutation.mutate({ p: prompt, answers });

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 dark:bg-gray-900 dark:border-gray-800 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Link to="/reports" className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-sm">
            ← Reports
          </Link>
          <span className="text-slate-300 dark:text-slate-700">/</span>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Build a report</h1>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Describe what you want to see in plain English. AI will build the report in seconds.
        </p>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left — prompt + result */}
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-slate-50 dark:bg-gray-950">
          {/* Prompt box */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-slate-200 dark:border-gray-800 p-5 mb-5">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              What do you want to see?
            </label>
            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && prompt.trim()) {
                  triggerBuild(prompt.trim());
                }
              }}
              placeholder="e.g. Show me total labor cost by project for this year, sorted by highest cost"
              rows={3}
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-slate-400 dark:text-slate-600">
                ⌘↩ to build
              </p>
              <button
                onClick={() => prompt.trim() && triggerBuild(prompt.trim())}
                disabled={!prompt.trim() || isPending}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition"
              >
                {buildMutation.isPending ? "Building…" : "✨ Build with AI"}
              </button>
            </div>
          </div>

          {/* Starter prompts */}
          {!result && !isPending && (
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mb-2">
                Or pick a starting point
              </p>
              <div className="flex flex-wrap gap-2">
                {STARTER_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => {
                      setPrompt(p);
                      triggerBuild(p);
                    }}
                    className="px-3 py-1.5 text-xs bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-full text-slate-600 dark:text-slate-400 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Loading state */}
          {isPending && (
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-slate-200 dark:border-gray-800 p-8 text-center">
              <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm text-slate-500 dark:text-slate-400">
                AI is building your report…
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-300 mb-4">
              {String((error as { message?: string }).message || error)}
            </div>
          )}

          {/* Clarifying questions */}
          {pendingQuestions && !isPending && (
            <ClarifyingQuestionsPanel
              questions={pendingQuestions}
              onSubmit={triggerBuildWithAnswers}
              isPending={buildMutation.isPending}
            />
          )}

          {/* Result */}
          {hasReport && !isPending && (
            <div className="space-y-4">
              {/* Explanation */}
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wider mb-1">
                  ✨ AI explanation
                </p>
                <p className="text-sm text-blue-900 dark:text-blue-100">{result.ai_explanation}</p>
                {result.schema_warnings.length > 0 && (
                  <div className="mt-2">
                    {result.schema_warnings.map((w, i) => (
                      <p key={i} className="text-xs text-amber-700 dark:text-amber-400">
                        ⚠ {w}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {/* Preview table */}
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-slate-200 dark:border-gray-800 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 dark:border-gray-800 flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Preview ({result.preview_rows.length} rows)
                  </span>
                </div>
                <PreviewTable columns={result.columns} rows={result.preview_rows} />
              </div>

              {/* Refine */}
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-slate-200 dark:border-gray-800 p-4">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Tweak the report
                </p>
                <div className="flex gap-2">
                  <input
                    value={refineInput}
                    onChange={(e) => setRefineInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && refineInput.trim()) {
                        refineMutation.mutate(refineInput.trim());
                      }
                    }}
                    placeholder="e.g. exclude tax codes, add a budget column"
                    className="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
                  />
                  <button
                    onClick={() => refineInput.trim() && refineMutation.mutate(refineInput.trim())}
                    disabled={!refineInput.trim() || refineMutation.isPending}
                    className="px-3 py-2 bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 dark:hover:bg-gray-700 disabled:opacity-50 text-slate-700 dark:text-slate-300 text-sm rounded-lg transition"
                  >
                    {refineMutation.isPending ? "…" : "Refine"}
                  </button>
                </div>
              </div>

              {/* Save */}
              {!showSaveDialog ? (
                <button
                  onClick={() => setShowSaveDialog(true)}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition"
                >
                  Save & Open Report →
                </button>
              ) : (
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-slate-200 dark:border-gray-800 p-4">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Save report</p>
                  <input
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    placeholder="Report name"
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100 mb-3"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => saveMutation.mutate()}
                      disabled={!saveName.trim() || saveMutation.isPending}
                      className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition"
                    >
                      {saveMutation.isPending ? "Saving…" : "Save"}
                    </button>
                    <button
                      onClick={() => setShowSaveDialog(false)}
                      className="px-4 py-2 bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 dark:hover:bg-gray-700 text-slate-700 dark:text-slate-300 text-sm rounded-lg transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
